//Intel Hex record types
const DATA = 0,
	END_OF_FILE = 1,
	EXT_SEGMENT_ADDR = 2,
	START_SEGMENT_ADDR = 3,
	EXT_LINEAR_ADDR = 4,
	START_LINEAR_ADDR = 5;

const EMPTY_VALUE = 0xff;

/**
 * Converts a hex string to an array of bytes.
 *
 * @param {string} hex - The hex string to convert.
 * @returns {number[]} - The array of bytes.
 */
const hexToBytes = (hex: string): number[] => {
	const bytes: number[] = [];

	for (let c = 0; c < hex.length; c += 2) {
		const parsedNum = parseInt(hex.substring(c, c + 2), 16);

		if (isNaN(parsedNum)) {
			throw new Error(`Invalid hex string at position ${c}, ${hex.substring(c, c + 2)} is not a hex number`);
		}

		bytes.push(parsedNum);
	}

	return bytes;
};

export type IntelHexResult = {
	data: Uint8Array;
	values: {
		key: string,
		value: number
	}[];
};

/**
 * Parses an Intel Hex string and returns the extracted data as a Uint8Array.
 *
 * @param {Buffer} dataBuffer - The Intel Hex string encoded as a Buffer.
 * @param {number} bufferSize - The size of the output buffer. Default is 65536.
 * @param {number} addressOffset - The offset to apply to the address values. Default is 0.
 * @return {object} An object with the parsed data stored in the `data` property as a Uint8Array.
 * @throws {Error} If the Intel Hex string is invalid or the checksums do not match.
 * @throws {Error} If the EOF record is missing or invalid.
 */
export function parseIntelHex(dataBuffer: Buffer, bufferSize: number, addressOffset: number): IntelHexResult {
	const outBuff = new Uint8Array(bufferSize || 65536);
	const decoder = new TextDecoder('utf-8');
	const data = decoder.decode(dataBuffer);
	outBuff.fill(EMPTY_VALUE);
	let lineNum = 0; //Line number in the Intel Hex string
	let pos = 0; //Current position in the Intel Hex string
	let highAddress = 0;
	const SMALLEST_LINE = 11;

	while (pos + SMALLEST_LINE <= data.length) {
		//Parse an entire line
		if (data.charAt(pos++) !== ':') throw new Error('Line ' + (lineNum + 1) + ' does not start with a colon (:).');
		else lineNum++;

		//Number of bytes (hex digit pairs) in the data field
		const dataLength = parseInt(data.substring(pos, pos + 2), 16);
		pos += 2;

		//Get 16-bit address (big-endian)
		const lowAddress = parseInt(data.substring(pos, pos + 4), 16);

		pos += 4;

		//Record type
		const recordType = parseInt(data.substring(pos, pos + 2), 16);
		pos += 2;

		//Data field (hex-encoded string)
		const dataField = data.substring(pos, pos + dataLength * 2);
		const dataFieldBuf = hexToBytes(dataField);

		pos += dataLength * 2;
		//Checksum
		const checksum = parseInt(data.substring(pos, pos + 2), 16);
		pos += 2;

		//Validate checksum
		let calcChecksum = (dataLength + (lowAddress >> 8) + lowAddress + recordType) & 0xff;

		for (let i = 0; i < dataLength; i++) {
			calcChecksum = (calcChecksum + dataFieldBuf[i]) & 0xff;
		}

		calcChecksum = (0x100 - calcChecksum) & 0xff;

		if (checksum !== calcChecksum) {
			throw new Error('Invalid checksum on line ' + lineNum + ': got ' + checksum + ', but expected ' + calcChecksum);
		}

		let absoluteAddress: number;

		//Parse the record based on its recordType
		switch (recordType) {
			case DATA:
				absoluteAddress = highAddress + lowAddress - addressOffset;

				//dataFieldBuf.copy(buf, absoluteAddress);
				outBuff.set(dataFieldBuf, absoluteAddress);

				break;
			case END_OF_FILE:
				if (dataLength !== 0) throw new Error('Invalid EOF record on line ' + lineNum + '.');
				const values = Array.from(outBuff).map((b: number, index: number) => ({ key: index.toString(16), value: b }))
				return {
					data: outBuff,
					values
				};

			case EXT_SEGMENT_ADDR:
				if (dataLength !== 2 || lowAddress !== 0)
					throw new Error('Invalid extended segment address record on line ' + lineNum + '.');
				highAddress = parseInt(dataField, 16) << 4;
				break;
			case START_SEGMENT_ADDR:
				if (dataLength !== 4 || lowAddress !== 0)
					throw new Error('Invalid start segment address record on line ' + lineNum + '.');
				break;
			case EXT_LINEAR_ADDR:
				if (dataLength !== 2 || lowAddress !== 0)
					throw new Error('Invalid extended linear address record on line ' + lineNum + '.');
				highAddress = parseInt(dataField, 16) << 16;
				break;
			case START_LINEAR_ADDR:
				if (dataLength !== 4 || lowAddress !== 0)
					throw new Error('Invalid start linear address record on line ' + lineNum + '.');
				break;
			default:
				throw new Error('Invalid record type (' + recordType + ') on line ' + lineNum);
		}

		//Advance to the next line
		if (data.charAt(pos) === '\r') {
			pos++;
		}

		if (data.charAt(pos) === '\n') pos++;
	}

	throw new Error('Unexpected end of input: missing or invalid EOF record.');
}
